import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { VmeCommonModule } from '../../vme-common.module';
import { TaskSequenceService } from '../../services/task-sequence.service';
import {
  ITaskSequenceJobOverviewDTO,
  ITaskSequenceTaskOverviewDTO,
} from '../../interfaces/task-sequence/task-sequence-interfaces';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';

@Component({
  selector: 'app-task-sequencing-editor',
  standalone: true,
  imports: [VmeCommonModule],
  templateUrl: './task-sequencing-editor.component.html',
  styleUrls: ['./task-sequencing-editor.component.css'],
})
export class TaskSequencingEditorComponent implements OnInit {
  @Input() jobId: number;
  @Input() visible: boolean = false;
  @Input() displayCombineNotification: boolean = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() saveTaskSequence = new EventEmitter<boolean>();

  jobOverview: ITaskSequenceJobOverviewDTO;

  constructor(
    public taskSequenceService: TaskSequenceService,
    public messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.getJobOverview();
  }

  closeDialog() {
    this.visibleChange.emit(false);
  }

  getJobOverview(): void {
    this.taskSequenceService
      .getJobOverview(this.jobId)
      .then((overview) => {
        this.jobOverview = overview;
        this.setTaskSequencingFlags();
        this.setOrderSequencingFlag();
      })
      .catch((error) => {
        if (error.status === 404) {
          this.messageService.add(Message.Error('Unable to find the current job.'));
        } else {
          this.messageService.add(
            Message.Error(
              'An error occurred while loading the sequencing overview for the current job.',
            ),
          );
        }

        this.closeDialog();
      });
  }

  setTaskSequencingFlags(): void {
    this.jobOverview.orders.forEach((order) => {
      order.taskSequencingEnabled = order.tasks.some((task) => task.sequence != null);
    });
  }

  setOrderSequencingFlag(): void {
    this.jobOverview.orderSequencingEnabled = this.jobOverview.orders.some(
      (order) => order.sequence != null,
    );
  }

  enableTaskSequencing(order: any): void {
    order.tasks.forEach((task, index) => {
      task.sequence = index + 1;
    });
  }

  disableTaskSequencing(order: any): void {
    order.tasks.forEach((task) => {
      task.sequence = null;
    });
  }

  enableOrderSequencing(): void {
    this.jobOverview.orders.forEach((order, index) => {
      order.sequence = index + 1;
    });
  }

  disableOrderSequencing(): void {
    this.jobOverview.orders.forEach((order) => {
      order.sequence = null;
    });
  }

  handleTaskSequenceToggle(event: any, order: any) {
    if (event.checked) {
      this.enableTaskSequencing(order);
    } else {
      this.disableTaskSequencing(order);
    }
  }

  handleOrderSequenceToggle(event: any): void {
    if (event.checked) {
      this.enableOrderSequencing();
    } else {
      this.disableOrderSequencing();
    }
  }

  moveOrderSequenceUp(orderIndex: number) {
    if (orderIndex > 0) {
      const orders = this.jobOverview.orders;
      [orders[orderIndex - 1], orders[orderIndex]] = [orders[orderIndex], orders[orderIndex - 1]];
      this.updateOrderSequenceIds();
    }
  }

  moveOrderSequenceDown(orderIndex: number) {
    if (orderIndex < this.jobOverview.orders.length - 1) {
      const orders = this.jobOverview.orders;
      [orders[orderIndex + 1], orders[orderIndex]] = [orders[orderIndex], orders[orderIndex + 1]];
      this.updateOrderSequenceIds();
    }
  }

  moveTaskSequenceUp(order: any, taskIndex: number) {
    if (taskIndex > 0) {
      const tasks = order.tasks;
      [tasks[taskIndex - 1], tasks[taskIndex]] = [tasks[taskIndex], tasks[taskIndex - 1]];
      this.updateTaskSequenceIds(order.tasks);
    }
  }

  moveTaskSequenceDown(order: any, taskIndex: number) {
    if (taskIndex < order.tasks.length - 1) {
      const tasks = order.tasks;
      [tasks[taskIndex + 1], tasks[taskIndex]] = [tasks[taskIndex], tasks[taskIndex + 1]];
      this.updateTaskSequenceIds(order.tasks);
    }
  }

  updateTaskSequenceIds(tasks: ITaskSequenceTaskOverviewDTO[]): void {
    tasks.forEach((task, i) => {
      task.sequence = i + 1;
    });
  }

  updateOrderSequenceIds(): void {
    this.jobOverview.orders.forEach((order, i) => {
      order.sequence = i + 1;
    });
  }

  saveChanges(): void {
    this.taskSequenceService.updateFullJobSequence(this.jobOverview).then(() => {
      this.messageService.add(Message.Success('Sequence updated successfully.'));
      this.saveTaskSequence.emit(true);
    });
  }
}
